import React, { useEffect, useMemo, useState } from 'react';

import * as Tabs from '@radix-ui/react-tabs';
import { ArrowLeft, Sparkles, Menu } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

// MUI imports removed - migrated to Tailwind/shadcn

import { NavigationItem } from './NavigationItem';
import { mapSidebarMarketplace } from 'components/Helper/Filter/mappings/sidebarMarketplace';
import ConfirmLogoutModal from 'components/Logout/components/ConfirmLogoutModal';
import { Button } from 'components/ui/button';
import { Card } from 'components/ui/card';
import { Separator } from 'components/ui/separator';
import { Sheet, SheetContent } from 'components/ui/sheet';
import { useMenu } from 'global/MenuContext';
import { useMenuSelection } from 'global/MenuSelectionContext';
import { useUser } from 'global/UserContext';
import { useMenuTitle } from 'hooks/useMenuTitleContext';
import { useSolutionsMenu } from 'hooks/useSolutionsMenu';
import { Icon, MeetingRoomExtras, MenuItem, isMeetingRoomExtras } from 'types/menuTypes';

const COCKPIT_DOCUMENT_ID = 'bhnrs7gefue5v5ntzqg3ls74';
const COCKPIT_AGENDA_DOCUMENT_ID = 'eh04q5jlhm52tnh1tthycrkd';

// Utility function to render a menu icon
const renderMenuIcon = (icon: any, alt: string, size = 24) => {
  if (Array.isArray(icon) && icon[0]?.url) {
    return <img src={icon[0].url} alt={alt} style={{ width: size, height: size }} />;
  }
  if (icon && typeof icon === 'object' && icon.url) {
    return <img src={icon.url} alt={alt} style={{ width: size, height: size }} />;
  }
  return <Menu style={{ width: size, height: size }} />;
};

// Finds the menu icon or the closest parent's icon in the hierarchy
const findMenuIcon = (menuItem: MenuItem, menuList: MenuItem[]): MenuItem['icon'] | null => {
  if (
    menuItem.icon &&
    (Array.isArray(menuItem.icon) ? menuItem.icon.length : (menuItem.icon as any).url)
  ) {
    return menuItem.icon;
  }
  for (const parent of menuList) {
    if (
      parent.subMenu?.some(
        (sub: MenuItem) => sub.code === menuItem.code || sub.documentId === menuItem.documentId
      )
    ) {
      return parent.icon;
    }
    if (parent.subMenu?.length) {
      const found = findMenuIcon(menuItem, parent.subMenu);
      if (found) return found;
    }
  }
  return null;
};

const MobileMenu: React.FC = () => {
  const { t } = useTranslation('common');
  const { menu } = useMenu();
  const { updateMenuTitle } = useMenuTitle();
  const navigate = useNavigate();
  const location = useLocation();
  const [navValue, setNavValue] = useState('menu');
  const [openMenu, setOpenMenu] = useState(false);
  const [selectedSecond, setSelectedSecond] = useState<MenuItem | null>(null);
  const [selectedFirstForThird, setSelectedFirstForThird] = useState<MenuItem | null>(null);
  const { logout } = useUser();
  const { sideMenu: sideMenuFromMenu } = useMenu();
  const [logoutModalOpen, setLogoutModalOpen] = useState(false);
  const { activeSolutions, hasActiveSolutions } = useSolutionsMenu();
  const {
    currentLevel,
    firstLevelMenu,
    secondLevelMenu,
    thirdLevelMenu,
    activeModulePath,
    activeDocumentId,
    selectMenuByDocumentId,
    selectMenuByPath,
  } = useMenuSelection();
  const [selectedMarketplaceItem, setSelectedMarketplaceItem] = useState<string | null>(null);
  const sideMenu = sideMenuFromMenu; // Use the one from MenuContext
  const [openMenus, setOpenMenus] = useState<{ [key: string]: boolean }>({});
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedMenu, setSelectedMenu] = useState<MenuItem | null>(null);

  // Function to find the first valid menu (recursive search)
  const findFirstValidMenu = (menuItems: MenuItem[]): MenuItem | null => {
    for (const menuItem of menuItems) {
      if (menuItem.isActive) {
        if (menuItem.subMenu?.length) {
          const found = findFirstValidMenu(menuItem.subMenu);
          if (found) return found;
        }
        return menuItem;
      }
    }
    return null;
  };

  const firstValidMenu = findFirstValidMenu(menu);

  useEffect(() => {
    updateMenuTitle(firstValidMenu?.title || 'Menu');
  }, [firstValidMenu, updateMenuTitle]);

  // Memoize menus to avoid unnecessary recalculations
  const {
    firstLevelMenus,
    groupedFirstLevelMenus,
    singleFirstLevelMenus,
    initialMenu,
    cockpitMenu,
    cockpitAgendaMenu,
    solutionsMenu,
  } = useMemo(() => {
    const firstLevelMenus: MenuItem[] = menu.filter(
      (item: MenuItem) => item.level === 'first' && item.isActive
    );

    // Find Cockpit-specific menus
    const cockpitMenu = firstLevelMenus.find(item => item.documentId === COCKPIT_DOCUMENT_ID);
    const cockpitAgendaMenu = firstLevelMenus.find(
      item => item.documentId === COCKPIT_AGENDA_DOCUMENT_ID
    );
    // Find the solutions menu
    const solutionsMenu = firstLevelMenus.find(item => item.label?.toLowerCase().includes('soluç'));

    // Prioritize Cockpit Agenda over Cockpit
    const initialMenu = cockpitAgendaMenu || cockpitMenu;

    // Menus with submenus (groups)
    const groupedFirstLevelMenus = firstLevelMenus.filter(
      item => Array.isArray(item.subMenu) && item.subMenu.length > 0
    );

    // Menus without submenus (simple items)
    const singleFirstLevelMenus = firstLevelMenus.filter(
      item => !item.subMenu || item.subMenu.length === 0
    );

    return {
      firstLevelMenus,
      groupedFirstLevelMenus,
      singleFirstLevelMenus,
      initialMenu,
      cockpitMenu,
      cockpitAgendaMenu,
      solutionsMenu,
    };
  }, [menu]);

  // Solutions menus filtered by path
  const solutionsMenusList = useMemo(() => {
    return firstLevelMenus.filter(
      item =>
        item.path &&
        (item.path.startsWith('/solucoes') || item.path.startsWith('/solutions')) &&
        item.isActive
    );
  }, [firstLevelMenus]);

  // Submenus of the solutions menu
  const solutionsSubMenus = useMemo(() => {
    const solutionsMenuItem = firstLevelMenus.find(
      item =>
        item.label && item.label.toLowerCase().includes('soluç') && item.subMenu && item.isActive
    );
    return solutionsMenuItem?.subMenu?.filter(sub => sub.isActive) || [];
  }, [firstLevelMenus]);

  // Sync navValue with the current route to ensure correct highlighting
  useEffect(() => {
    if (initialMenu && location.pathname === initialMenu.path) {
      setNavValue('initial');
    } else if (location.pathname === '/menu') {
      setNavValue('menu');
    } else if (location.pathname.startsWith('/solutions')) {
      setNavValue('solutions');
    }
    // Adapt for other cases if necessary
  }, [location.pathname, initialMenu]);

  const marketplaceItems = useMemo(() => mapSidebarMarketplace(sideMenu), [sideMenu]);

  // Effect to open the active menu when the level changes
  useEffect(() => {
    if (currentLevel === 'first' && firstLevelMenu) {
      setOpenMenus(prev => ({ ...prev, [firstLevelMenu.documentId || '']: true }));
    } else if (currentLevel === 'second' && secondLevelMenu) {
      setOpenMenus(prev => ({ ...prev, [secondLevelMenu.documentId || '']: true }));
    } else if (currentLevel === 'third' && thirdLevelMenu) {
      setOpenMenus(prev => ({ ...prev, [thirdLevelMenu.documentId || '']: true }));
    }
  }, [currentLevel, firstLevelMenu, secondLevelMenu, thirdLevelMenu]);

  // Handlers
  const handleTabChange = (newValue: string) => {
    setNavValue(newValue);
    if (newValue === 'menu' || newValue === 'solutions') {
      setDrawerOpen(true);
      setSelectedSecond(null);
      setSelectedFirstForThird(null);
    } else {
      // Handle Cockpit/Cockpit Agenda navigation
      const menuItem = menu?.find(item => item.documentId === newValue);
      if (menuItem) {
        selectMenuByPath(menuItem.path);
        navigate(menuItem.path);
        setDrawerOpen(false);
      }
    }
  };

  const handleFirstLevelClick = (item: MenuItem) => {
    if (item.subMenu && item.subMenu.length > 0) {
      setSelectedSecond(item);
      setNavValue('submenu');
      setDrawerOpen(false);
      return;
    }
    if (item.feature?.documentId) {
      selectMenuByDocumentId(item.feature.documentId, true, undefined, item.path);
      navigate(item.path);
    } else if (
      isMeetingRoomExtras(item.extras ?? null) &&
      typeof (item.extras as MeetingRoomExtras).internal_id === 'string'
    ) {
      selectMenuByDocumentId(
        (item.extras as MeetingRoomExtras).internal_id!,
        true,
        undefined,
        item.path
      );
      navigate(item.path);
    }
    setOpenMenu(false);
    setDrawerOpen(false);
  };

  const handleSecondLevelClick = (item: MenuItem, parent: MenuItem) => {
    if (item.subMenu && item.subMenu.length > 0) {
      const firstThird = item.subMenu[0];
      if (firstThird.feature?.documentId) {
        selectMenuByDocumentId(firstThird.feature.documentId, true, undefined, firstThird.path);
        navigate(firstThird.path);
      } else if (
        isMeetingRoomExtras(firstThird.extras ?? null) &&
        typeof (firstThird.extras as MeetingRoomExtras).internal_id === 'string'
      ) {
        selectMenuByDocumentId(
          (firstThird.extras as MeetingRoomExtras).internal_id!,
          true,
          undefined,
          firstThird.path
        );
        navigate(firstThird.path);
      }
      setOpenMenu(false);
      setDrawerOpen(false);
      return;
    }
    if (
      item.feature?.dynamic_component === 'MEETING_ROOM' &&
      item.subMenu &&
      item.subMenu.length > 0
    ) {
      const firstSubMenu = item.subMenu[0];
      if (firstSubMenu) {
        if (firstSubMenu.feature?.documentId) {
          selectMenuByDocumentId(
            firstSubMenu.feature.documentId,
            true,
            undefined,
            firstSubMenu.path
          );
          navigate(firstSubMenu.path);
        } else if (
          isMeetingRoomExtras(firstSubMenu.extras ?? null) &&
          typeof (firstSubMenu.extras as MeetingRoomExtras).internal_id === 'string'
        ) {
          selectMenuByDocumentId(
            (firstSubMenu.extras as MeetingRoomExtras).internal_id!,
            true,
            undefined,
            firstSubMenu.path
          );
          navigate(firstSubMenu.path);
        }
        setOpenMenu(false);
        setDrawerOpen(false);
        return;
      }
    }
    if (item.feature?.documentId) {
      selectMenuByDocumentId(item.feature.documentId, true, undefined, item.path);
      navigate(item.path);
    } else if (
      isMeetingRoomExtras(item.extras ?? null) &&
      typeof (item.extras as MeetingRoomExtras).internal_id === 'string'
    ) {
      selectMenuByDocumentId(
        (item.extras as MeetingRoomExtras).internal_id!,
        true,
        undefined,
        item.path
      );
      navigate(item.path);
    }
    setOpenMenu(false);
    setDrawerOpen(false);
  };

  const handleThirdLevelClick = (
    item: MenuItem,
    parent: MenuItem | null,
    parentSecondLevel: MenuItem | null
  ) => {
    if (item.feature?.documentId) {
      selectMenuByDocumentId(item.feature.documentId, true, undefined, item.path);
      navigate(item.path);
    } else if (
      isMeetingRoomExtras(item.extras ?? null) &&
      typeof (item.extras as MeetingRoomExtras).internal_id === 'string'
    ) {
      selectMenuByDocumentId(
        (item.extras as MeetingRoomExtras).internal_id!,
        true,
        undefined,
        item.path
      );
      navigate(item.path);
    }
    setOpenMenu(false);
    setDrawerOpen(false);
  };

  const handleBackToSecond = () => {
    setSelectedSecond(null);
    setSelectedFirstForThird(null);
  };

  const handleOpenLogoutModal = () => setLogoutModalOpen(true);
  const handleCloseLogoutModal = () => setLogoutModalOpen(false);
  const handleConfirmLogout = () => {
    setLogoutModalOpen(false);
    setTimeout(() => {
      logout();
    }, 300);
  };

  const handleMarketplaceItemClick = (type: string) => {
    const routeMap: Record<string, { path: string; titleKey: string }> = {
      advisor: { path: '/marketplace/assessores', titleKey: 'marketplace.advisors' },
      courses: { path: '/marketplace/cursos', titleKey: 'marketplace.courses' },
      services: { path: '/marketplace/servicos', titleKey: 'marketplace.services' },
      plans: { path: '/marketplace/planos', titleKey: 'marketplace.plans' },
    };

    const route = routeMap[type];
    if (route) {
      updateMenuTitle(t(route.titleKey));
      navigate(route.path);
      setOpenMenu(false);
    }
  };

  const handleClick = (item: MenuItem) => {
    setOpenMenus(prev => ({ ...prev, [item.documentId || '']: !prev[item.documentId || ''] }));
  };

  const handleItemClick = (item: MenuItem) => {
    selectMenuByPath(item.path || '/');
    navigate(item.path || '/');
    setDrawerOpen(false);
  };

  const handleMenuClick = (item: MenuItem) => {
    if (item.subMenu && item.subMenu.length > 0) {
      setSelectedMenu(item);
      setDrawerOpen(true);
    } else {
      handleItemClick(item);
    }
  };

  // Function to render icon, whether string, object, or array
  const renderIcon = (icon: Icon | Icon[] | string | undefined, label: string) => {
    if (!icon) return null;
    if (typeof icon === 'string') {
      return <img src={icon} alt={label} style={{ width: 24, height: 24 }} />;
    }
    if (Array.isArray(icon)) {
      if (icon.length > 0 && icon[0].url) {
        return (
          <img src={icon[0].url} alt={icon[0].name || label} style={{ width: 24, height: 24 }} />
        );
      }
      return null;
    }
    if (typeof icon === 'object' && icon.url) {
      return <img src={icon.url} alt={icon.name || label} style={{ width: 24, height: 24 }} />;
    }
    return null;
  };

  const renderMenuItem = (item: MenuItem, level: 'first' | 'second' | 'third' = 'first') => {
    const hasChildren = item.subMenu && item.subMenu.length > 0;
    const isOpen = openMenus[item.documentId || ''] || false;
    const isSelected = item.documentId === activeDocumentId;

    return (
      <React.Fragment key={item.documentId}>
        <NavigationItem
          menuItem={{
            id: item.documentId || item.code || 'menu-item',
            name: item.label || '',
            icon: item.icon ? renderIcon(item.icon, item.label) : undefined,
          }}
          onClick={() => (hasChildren ? handleClick(item) : handleItemClick(item))}
          isSelected={isSelected}
          level={level}
        />
      </React.Fragment>
    );
  };

  const renderMenuGroups = () => (
    <div className="p-4">
      {/* Cockpit/Cockpit Agenda highlighted at the top, matching the prototype */}
      {(cockpitAgendaMenu || cockpitMenu) && (
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-2">
            {renderMenuIcon(
              (cockpitAgendaMenu || cockpitMenu)?.icon,
              (cockpitAgendaMenu || cockpitMenu)?.label || ''
            )}
            <span className="text-base font-medium">
              {(cockpitAgendaMenu || cockpitMenu)?.label || ''}
            </span>
            <Separator className="flex-1" />
          </div>
          <Card
            className="w-[48%] p-4 text-center cursor-pointer mb-2 bg-card hover:bg-accent transition-colors"
            onClick={() => {
              const menuItem = cockpitAgendaMenu || cockpitMenu;
              if (menuItem) handleFirstLevelClick(menuItem);
            }}
          >
            {renderMenuIcon(
              (cockpitAgendaMenu || cockpitMenu)?.icon,
              (cockpitAgendaMenu || cockpitMenu)?.label || ''
            )}
            <span className="text-base font-medium block mt-1">
              {(cockpitAgendaMenu || cockpitMenu)?.label || ''}
            </span>
          </Card>
        </div>
      )}
      {/* Simple first-level menus (without submenus), except Cockpit/Cockpit Agenda */}
      {singleFirstLevelMenus
        .filter(item => item !== cockpitMenu && item !== cockpitAgendaMenu)
        .map(item => (
          <div
            key={
              item.feature?.documentId ||
              (isMeetingRoomExtras(item.extras ?? null) &&
              typeof (item.extras as MeetingRoomExtras).internal_id === 'string'
                ? (item.extras as MeetingRoomExtras).internal_id!
                : item.code)
            }
            className="mb-4"
          >
            <Separator className="mb-4" />
            <Card
              className="w-full p-4 text-center cursor-pointer bg-card hover:bg-accent transition-colors"
              onClick={() => handleFirstLevelClick(item)}
            >
              {renderMenuIcon(item.icon, item.label || '')}
              <span className="text-base font-medium block mt-1">{item.label || ''}</span>
            </Card>
          </div>
        ))}
      {/* First-level groups (with submenus) */}
      {groupedFirstLevelMenus.map(group => (
        <div
          key={
            group.feature?.documentId ||
            (isMeetingRoomExtras(group.extras ?? null) &&
            typeof (group.extras as MeetingRoomExtras).internal_id === 'string'
              ? (group.extras as MeetingRoomExtras).internal_id!
              : group.code)
          }
          className="mb-8"
        >
          <div className="flex items-center gap-2 mb-2">
            {renderMenuIcon(group.icon, group.label || '')}
            <span className="text-base font-medium">{group.label || ''}</span>
            <Separator className="flex-1" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            {(group.subMenu || []).map(item => (
              <Card
                key={
                  item.feature?.documentId ||
                  (isMeetingRoomExtras(item.extras ?? null) &&
                  typeof (item.extras as MeetingRoomExtras).internal_id === 'string'
                    ? (item.extras as MeetingRoomExtras).internal_id!
                    : item.code)
                }
                className="p-4 text-center cursor-pointer bg-card hover:bg-accent transition-colors"
                onClick={() => handleSecondLevelClick(item, group)}
              >
                {renderMenuIcon(item.icon, item.label || '', 28)}
                <span className="text-sm block mt-1">{item.label || ''}</span>
              </Card>
            ))}
          </div>
        </div>
      ))}
    </div>
  );

  const renderThirdLevel = () => (
    <div className="flex flex-col items-center gap-1">
      {/* Header replacing AppBar/Toolbar */}
      <div className="w-full bg-card shadow-sm rounded-t-2xl">
        <div className="flex items-center px-4 py-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={handleBackToSecond}
            aria-label="back"
            className="mr-2"
          >
            <ArrowLeft className="w-6 h-6" />
          </Button>
          <h6 className="flex-1 text-lg font-medium">{selectedSecond?.label}</h6>
        </div>
      </div>
      <Separator />
      {/* List replacing MUI List */}
      <div className="w-full">
        {(selectedSecond?.subMenu || []).map((item, idx) => (
          <NavigationItem
            key={
              item.feature?.documentId ||
              (isMeetingRoomExtras(item.extras ?? null) &&
              typeof (item.extras as MeetingRoomExtras).internal_id === 'string'
                ? (item.extras as MeetingRoomExtras).internal_id!
                : item.code)
            }
            menuItem={{
              id:
                item.feature?.documentId ||
                (isMeetingRoomExtras(item.extras ?? null) &&
                typeof (item.extras as MeetingRoomExtras).internal_id === 'string'
                  ? (item.extras as MeetingRoomExtras).internal_id!
                  : item.code) ||
                'menu-item',
              name: item.label || '',
              icon: item.icon ? renderIcon(item.icon, item.label) : undefined,
            }}
            onClick={() => handleThirdLevelClick(item, selectedSecond, null)}
            isSelected={idx === 0}
            level="third"
          />
        ))}
      </div>
    </div>
  );

  const renderSolutionsMenu = () => (
    <div className="p-4">
      <div className="flex items-center gap-2 mb-2">
        <Sparkles className="w-6 h-6" />
        <span className="text-base font-medium">{t('navigation.solutions')}</span>
        <Separator className="flex-1" />
      </div>
      <div className="grid grid-cols-2 gap-4">
        {solutionsSubMenus.map(item => (
          <Card
            key={item.documentId || item.path}
            className="p-4 text-center cursor-pointer bg-card hover:bg-accent transition-colors"
            onClick={() => {
              selectMenuByPath(item.path);
              navigate(item.path);
              setDrawerOpen(false);
            }}
          >
            <Sparkles className="w-7 h-7 mx-auto" />
            <span className="text-sm block mt-1">{item.label || ''}</span>
          </Card>
        ))}
      </div>
    </div>
  );

  // Update the menu title when a marketplace item is selected
  useEffect(() => {
    if (selectedMarketplaceItem) {
      const selectedItem = marketplaceItems.find(item => item.type === selectedMarketplaceItem);
      if (selectedItem) {
        updateMenuTitle(selectedItem.name);
      }
    }
  }, [selectedMarketplaceItem, marketplaceItems, updateMenuTitle]);

  return (
    <>
      <Tabs.Root
        value={selectedMenu?.documentId || navValue}
        onValueChange={handleTabChange}
        className="fixed left-0 right-0 z-[1200] h-[7vh] bg-background"
      >
        <Tabs.List className="flex h-full border-t border-border">
          {/* Cockpit/Cockpit Agenda (if exists) */}
          {(cockpitAgendaMenu || cockpitMenu) && (
            <Tabs.Trigger
              value={(cockpitAgendaMenu || cockpitMenu)?.documentId || ''}
              className="flex flex-1 flex-col items-center justify-center border-t-4 border-transparent data-[state=active]:border-primary min-w-0"
            >
              {renderMenuIcon(
                (cockpitAgendaMenu || cockpitMenu)?.icon,
                (cockpitAgendaMenu || cockpitMenu)?.label || ''
              )}
              <span className="text-sm text-foreground">
                {(cockpitAgendaMenu || cockpitMenu)?.label || ''}
              </span>
            </Tabs.Trigger>
          )}
          {/* Menu Button */}
          <Tabs.Trigger
            value="menu"
            className="flex flex-1 flex-col items-center justify-center border-t-4 border-transparent data-[state=active]:border-primary min-w-0"
          >
            <Menu className="w-8 h-8 text-primary" />
            <span className="text-sm text-foreground">Menu</span>
          </Tabs.Trigger>
        </Tabs.List>
      </Tabs.Root>

      <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
        <SheetContent
          side="bottom"
          className="rounded-t-2xl min-h-[30vh] max-h-[85vh] bg-background"
        >
          {!selectedSecond && navValue === 'menu' && renderMenuGroups()}
          {!selectedSecond && navValue === 'solutions' && renderSolutionsMenu()}
          {selectedSecond && renderThirdLevel()}
          <div className="p-4 pb-6">
            <Button onClick={handleOpenLogoutModal} variant="ghost" fullWidth>
              {t('sidebar.logoutPlatform')}
            </Button>
          </div>

          <ConfirmLogoutModal
            open={logoutModalOpen}
            onClose={handleCloseLogoutModal}
            onConfirm={handleConfirmLogout}
          />
        </SheetContent>
      </Sheet>
    </>
  );
};

export default MobileMenu;
